import { useState, useCallback, useEffect } from 'react'
import { Card } from '../Common/Card'
import { Button } from '../Common/Button'
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  Eye,
  EyeOff,
  AlertCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../../services/api'

export const SheetGallery = ({ sheets }) => {
  const [selectedSheet, setSelectedSheet] = useState(0)
  const [zoom, setZoom] = useState(100)
  const [showData, setShowData] = useState(true)
  const [imageError, setImageError] = useState(false)

  if (!sheets || sheets.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-center text-gray-600">Листы отсутствуют</p>
        </div>
      </Card>
    )
  }

  const currentSheet = sheets[selectedSheet]

  const getSheetTypeLabel = (type) => {
    const labels = {
      'materials': 'Материалы',
      'assembly': 'Сборка',
      'operational': 'Операции',
      'unknown': 'Неизвестно'
    }
    return labels[type] || type
  }

  const getSheetTypeColor = (type) => {
    const colors = {
      'materials': 'bg-blue-100 text-blue-800',
      'assembly': 'bg-green-100 text-green-800',
      'operational': 'bg-purple-100 text-purple-800',
      'unknown': 'bg-gray-100 text-gray-800'
    }
    return colors[type] || colors['unknown']
  }

  const handlePrevSheet = useCallback(() => {
    setSelectedSheet(prev => Math.max(0, prev - 1))
    setImageError(false)
  }, [])

  const handleNextSheet = useCallback(() => {
    setSelectedSheet(prev => Math.min(sheets.length - 1, prev + 1))
    setImageError(false)
  }, [sheets.length])

  const handleZoomIn = () => {
    setZoom(prev => Math.min(200, prev + 10))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(20, prev - 10))
  }

  const handleDownloadImage = async () => {
    // Используем новый endpoint для скачивания аннотированного изображения
    const downloadUrl = currentSheet.annotated_image_url || currentSheet.image_url
    
    if (!downloadUrl) {
      toast.error('Изображение недоступно')
      return
    }

    try {
      // Если есть presigned URL - используем его напрямую
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `sheet_${currentSheet.page_number}.png`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('Изображение скачивается...')
    } catch (error) {
      toast.error('Ошибка при скачивании изображения')
      console.error(error)
    }
  }

  const handleImageError = () => {
    setImageError(true)
    toast.error('Ошибка при загрузке изображения')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') handlePrevSheet()
    if (e.key === 'ArrowRight') handleNextSheet()
  }

  // Получаем URL изображения из S3
  const getImageUrl = () => {
    // Приоритет: presigned URL > прокси через backend
    if (currentSheet.image_url) {
      return currentSheet.image_url
    }
    // Fallback: прокси через backend
    return `/api/technical_cards/${currentSheet.id}/image`
  }

  return (
    <div className="space-y-4" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Основное изображение */}
      <Card className="flex flex-col">
        {/* Заголовок */}
        <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Лист {selectedSheet + 1} из {sheets.length}
              </h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSheetTypeColor(currentSheet.sheet_type)}`}>
                {getSheetTypeLabel(currentSheet.sheet_type)}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Страница {currentSheet.page_number}
            </p>
          </div>

          {/* Контролы зума */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 10}
              title="Уменьшить масштаб (Ctrl + -)"
              className="p-2"
            >
              <ZoomOut size={18} />
            </Button>
            <span className="px-3 py-1 bg-white rounded text-sm font-medium text-gray-900 min-w-16 text-center">
              {zoom}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 200}
              title="Увеличить масштаб (Ctrl + +)"
              className="p-2"
            >
              <ZoomIn size={18} />
            </Button>
          </div>
        </div>

        {/* Изображение из S3 */}
        <div className="w-full bg-gray-100 rounded-lg overflow-auto flex items-center justify-center min-h-96 mb-4 border-2 border-gray-200">
          {imageError ? (
            <div className="text-center py-12">
              <AlertCircle size={48} className="mx-auto text-red-600 mb-4" />
              <p className="text-gray-600">Ошибка при загрузке изображения</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setImageError(false)}
                className="mt-4"
              >
                Попробовать снова
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center p-4">
              <img 
                src={getImageUrl()}
                alt={`Sheet ${currentSheet.page_number}`}
                style={{
                  width: `${zoom}%`,
                  height: 'auto',
                  maxHeight: '150%'
                }}
                className="object-contain"
                onError={handleImageError}
                loading="lazy"
                crossOrigin="anonymous"
              />
            </div>
          )}
        </div>

        {/* Кнопки действий */}
        <div className="flex gap-2 justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowData(!showData)}
            className="flex items-center gap-2"
            title={showData ? 'Скрыть данные' : 'Показать данные'}
          >
            {showData ? <EyeOff size={18} /> : <Eye size={18} />}
            {showData ? 'Скрыть' : 'Показать'} данные
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadImage}
            className="flex items-center gap-2"
            title="Скачать изображение"
          >
            <Download size={18} />
            Скачать
          </Button>
        </div>

        {/* Информация о листе */}
        {showData && currentSheet.operations_data && Object.keys(currentSheet.operations_data).length > 0 && (
          <div className="w-full mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold mb-3 text-gray-900">Извлеченные данные</h4>
            <SheetDataViewer data={currentSheet.operations_data} />
          </div>
        )}

        {/* Информация о верификации */}
        {currentSheet.verification_status && (
          <div className="w-full mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Статус верификации:</span> {currentSheet.verification_status}
            </p>
          </div>
        )}
      </Card>

      {/* Навигация по листам */}
      <Card className="flex flex-col gap-4">
        {/* Кнопки навигации */}
        <div className="flex items-center justify-between">
          <Button
            variant="secondary"
            onClick={handlePrevSheet}
            disabled={selectedSheet === 0}
            className="flex items-center gap-2"
            title="Предыдущий лист (←)"
          >
            <ChevronLeft size={20} />
            Предыдущий
          </Button>

          <span className="text-sm text-gray-600 font-medium">
            {selectedSheet + 1} из {sheets.length}
          </span>

          <Button
            variant="secondary"
            onClick={handleNextSheet}
            disabled={selectedSheet === sheets.length - 1}
            className="flex items-center gap-2"
            title="Следующий лист (→)"
          >
            Следующий
            <ChevronRight size={20} />
          </Button>
        </div>

        {/* Миниатюры листов */}
        <div className="flex gap-2 flex-wrap justify-center">
          {sheets.map((sheet, index) => (
            <button
              key={index}
              onClick={() => {
                setSelectedSheet(index)
                setImageError(false)
              }}
              className={`px-3 py-2 rounded font-medium transition-all duration-200 ${
                selectedSheet === index
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              title={`Лист ${index + 1} - ${getSheetTypeLabel(sheet.sheet_type)}`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </Card>
    </div>
  )
}

// Компонент для отображения данных листа
function SheetDataViewer({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-gray-600 text-sm">Нет данных для отображения</p>
  }

  return (
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="border-l-4 border-blue-500 pl-4">
          <h5 className="font-semibold text-gray-900 capitalize mb-2">{formatKey(key)}</h5>
          <DataContent value={value} />
        </div>
      ))}
    </div>
  )
}

// Компонент для отображения содержимого данных
function DataContent({ value }) {
  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.map((item, idx) => (
          <div key={idx} className="bg-white p-3 rounded border border-gray-200 text-sm">
            {typeof item === 'object' ? (
              <ul className="space-y-1">
                {Object.entries(item).map(([k, v]) => (
                  <li key={k} className="text-gray-700">
                    <span className="font-medium text-gray-900">{formatKey(k)}:</span> {String(v)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-700">{String(item)}</p>
            )}
          </div>
        ))}
      </div>
    )
  }

  if (typeof value === 'object') {
    return (
      <div className="bg-white p-3 rounded border border-gray-200 text-sm">
        <ul className="space-y-1">
          {Object.entries(value).map(([k, v]) => (
            <li key={k} className="text-gray-700">
              <span className="font-medium text-gray-900">{formatKey(k)}:</span> {String(v)}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return <p className="text-gray-700 text-sm bg-white p-3 rounded border border-gray-200">{String(value)}</p>
}

// Вспомогательная функция для форматирования ключей
function formatKey(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}
